<script lang="ts" setup>
import { computed, onMounted, watch } from "vue";
import { useBreakpoints } from "@vueuse/core";
import { useRouter, useRoute } from "vue-router";
import { PTag, PSideBar } from "@profeat/ui-kit";

import { useUser } from "~/stores/user.store";
import { useChannels } from "~/stores/channel.store";
import { useAuth } from "~/stores/auth.store";
import { useCompany } from "~/stores/company.store";
import { useI18n } from "vue-i18n";
import { useSidebar } from "~/composables/useSidebar";
import {
  createMainMenu,
  createBottomMenu,
  filterMenuByUserType,
  ROUTE_TO_SIDEBAR_MAP,
  SIDEBAR_ITEMS,
  type SidebarItem,
} from "./menu";

const router = useRouter();
const route = useRoute();
const { collapse, isVisible } = useBreakpoints({
  collapse: 1500,
  isVisible: 1024,
});
const userStore = useUser();
const channelsStore = useChannels();
const { logout, getUserPhone } = useAuth();
const { t } = useI18n();
const { state, setSelected, setIsOpened } = useSidebar();

const title = computed<string>(() => {
  return userStore.user.fancyId;
});

const hasProfileAlert = computed(() => {
  const noChannels = channelsStore.getChannels.length < 1;
  const alerts = [noChannels];

  return alerts.some(Boolean);
});

const menuItems = computed(() => {
  const mainMenu = createMainMenu(t, hasProfileAlert.value);
  const bottomMenu = createBottomMenu(t, supportTgLink.value);

  return {
    menu: filterMenuByUserType(mainMenu, userStore.user.userType),
    bottom: bottomMenu,
  };
});

const SUPPORT_TG_URL = "https://t.me/care_sendbot";
const { getCompanyId } = useCompany();
const supportTgLink = computed(
  () => `${SUPPORT_TG_URL}?start=${getUserPhone}_${getCompanyId}`,
);

onMounted(() => {
  const currentItem = route.path.split(
    "/",
  )[1] as SidebarItem;

  if (SIDEBAR_ITEMS.includes(currentItem)) {
    setSelected(currentItem);
  }
});

watch(collapse, () => {
  setIsOpened(collapse.value);
});

watch(isVisible, () => {
  const value = isVisible.value && collapse.value;
  setIsOpened(value);
});
watch(
  () => router.currentRoute.value,
  (value) => {
    const routeNameMatch =
      ROUTE_TO_SIDEBAR_MAP[value.name as keyof typeof ROUTE_TO_SIDEBAR_MAP];
    if (routeNameMatch) {
      setSelected(routeNameMatch);
    }
  },
);

const handleMenuItemClick = async (event: { value: string }) => {
  const { value } = event;

  if (value === "logout") {
    await logout();
    await router.push("/login");
    return;
  }

  if (value !== "link") {
    setSelected(value);
  }
};
</script>

<template>
  <div class="sidebar">
    <PSideBar
      v-model:selected="state.selected"
      v-model:opened="state.isOpened"
      :items="menuItems"
      :title="title"
      :padding="'medium'"
      :product="'sendbot'"
      @click:item="handleMenuItemClick"
    >
      <template #right-side-item="{ item }">
        <PTag
          v-if="item.description"
          color="blue"
          class="font-weight-regular text-14px"
        >
          {{ item.description }}
        </PTag>
      </template>
    </PSideBar>
  </div>
</template>

<style lang="scss" scoped>
.sidebar {
  border-right: 1px solid #2b323814;

  :deep(.item-wrapper) {
    align-items: baseline;
  }
}
</style>
